import React, { useState, useEffect, useCallback } from "react"
import {
    Box,
    Typography,
    Switch,
    Button,
    Chip,
    Alert,
    Snackbar,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material"
import PaymentIcon from "@mui/icons-material/Payment"
import SaveIcon from "@mui/icons-material/Save"
import AddIcon from "@mui/icons-material/Add"
import CreditCardIcon from "@mui/icons-material/CreditCard"
import PaymentsIcon from "@mui/icons-material/Payments"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import LocalOfferIcon from "@mui/icons-material/LocalOffer"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import {
    getOutletPaymentMethods,
    updateOutletPaymentMethods,
} from "../../services/outletSettingsService"
import {
    createPaymentMethod,
    updatePaymentMethod,
} from "../../services/adminService"
import { useTranslation } from "react-i18next"

interface PaymentMethodsSectionProps {
    companyId: string
    outletId: string
}

interface PaymentMethodRow {
    id: string
    code: string
    label: string
    type: string
    is_active: boolean
    is_enabled: boolean
    display_order: number
}

const typeColors: Record<string, string> = {
    cash: "#16a34a",
    card: "#2563eb",
    check: "#d97706",
    voucher: "#7c3aed",
    transfer: "#0891b2",
    other: "#6b7280",
}

const paymentTypeValues = [
    "cash",
    "card",
    "check",
    "transfer",
    "voucher",
    "other",
]

const typeIcons: Record<string, React.ReactNode> = {
    cash: <PaymentsIcon sx={{ fontSize: 18 }} />,
    card: <CreditCardIcon sx={{ fontSize: 18 }} />,
    check: <ReceiptLongIcon sx={{ fontSize: 18 }} />,
    transfer: <SwapHorizIcon sx={{ fontSize: 18 }} />,
    voucher: <LocalOfferIcon sx={{ fontSize: 18 }} />,
    other: <PaymentIcon sx={{ fontSize: 18 }} />,
}

const toPaymentCode = (input: string) =>
    input
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 40)

const PaymentMethodsSection: React.FC<PaymentMethodsSectionProps> = ({
    companyId,
    outletId,
}) => {
    const { t } = useTranslation()
    const [methods, setMethods] = useState<PaymentMethodRow[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [creating, setCreating] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [originalMethods, setOriginalMethods] = useState<
        Record<string, { label: string; is_enabled: boolean }>
    >({})
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [draggedMethodId, setDraggedMethodId] = useState<string | null>(null)
    const [createForm, setCreateForm] = useState({
        code: "",
        label: "",
        type: "cash",
    })
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error"
    }>({ open: false, message: "", severity: "success" })

    const getPaymentTypeLabel = useCallback(
        (type: string): string =>
            String(t(`settings.paymentMethods.types.${type}`)),
        [t]
    )

    const loadMethods = useCallback(async () => {
        if (!companyId || !outletId) return
        setLoading(true)
        try {
            const data = await getOutletPaymentMethods(companyId, outletId)
            const rows = data || []
            setMethods(rows)
            setOriginalMethods(
                rows.reduce(
                    (
                        acc: Record<
                            string,
                            { label: string; is_enabled: boolean }
                        >,
                        method: PaymentMethodRow
                    ) => {
                        acc[method.id] = {
                            label: method.label || "",
                            is_enabled: !!method.is_enabled,
                        }
                        return acc
                    },
                    {}
                )
            )
            setDirty(false)
        } catch (e: any) {
            setSnackbar({
                open: true,
                message: String(t("settings.paymentMethods.toast.loadError")),
                severity: "error",
            })
        } finally {
            setLoading(false)
        }
    }, [companyId, outletId, t])

    useEffect(() => {
        loadMethods()
    }, [loadMethods])

    const handleToggle = (methodId: string) => {
        setMethods((prev) =>
            prev.map((m) =>
                m.id === methodId ? { ...m, is_enabled: !m.is_enabled } : m
            )
        )
        setDirty(true)
    }

    const handleLabelChange = (methodId: string, label: string) => {
        setMethods((prev) =>
            prev.map((m) => (m.id === methodId ? { ...m, label } : m))
        )
        setDirty(true)
    }

    const handleDragStart = (methodId: string) => {
        setDraggedMethodId(methodId)
    }

    const handleDrop = (targetMethodId: string) => {
        if (!draggedMethodId || draggedMethodId === targetMethodId) {
            setDraggedMethodId(null)
            return
        }

        setMethods((prev) => {
            const sourceIndex = prev.findIndex((m) => m.id === draggedMethodId)
            const targetIndex = prev.findIndex((m) => m.id === targetMethodId)

            if (sourceIndex < 0 || targetIndex < 0) return prev

            const next = [...prev]
            const [dragged] = next.splice(sourceIndex, 1)
            next.splice(targetIndex, 0, dragged)
            return next
        })
        setDirty(true)
        setDraggedMethodId(null)
    }

    const handleOpenCreateDialog = () => {
        setCreateForm({ code: "", label: "", type: "cash" })
        setShowCreateDialog(true)
    }

    const handleCreateMethod = async () => {
        const label = createForm.label.trim()
        const code = (
            createForm.code.trim() || toPaymentCode(label)
        ).toLowerCase()

        if (!label) {
            setSnackbar({
                open: true,
                message: String(
                    t("settings.paymentMethods.toast.labelRequired")
                ),
                severity: "error",
            })
            return
        }

        if (!code) {
            setSnackbar({
                open: true,
                message: String(
                    t("settings.paymentMethods.toast.codeRequired")
                ),
                severity: "error",
            })
            return
        }

        setCreating(true)
        try {
            await createPaymentMethod(companyId, {
                code,
                label,
                type: createForm.type,
                is_active: true,
            })
            setShowCreateDialog(false)
            await loadMethods()
            setSnackbar({
                open: true,
                message: String(t("settings.paymentMethods.toast.created")),
                severity: "success",
            })
        } catch (e: any) {
            setSnackbar({
                open: true,
                message:
                    e?.message ||
                    String(t("settings.paymentMethods.toast.createError")),
                severity: "error",
            })
        } finally {
            setCreating(false)
        }
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const labelUpdates = methods.filter((m) => {
                const initial = originalMethods[m.id]
                return (
                    initial &&
                    m.label.trim().length > 0 &&
                    m.label.trim() !== initial.label.trim()
                )
            })

            if (methods.some((m) => m.label.trim().length === 0)) {
                setSnackbar({
                    open: true,
                    message: String(
                        t("settings.paymentMethods.toast.labelEmpty")
                    ),
                    severity: "error",
                })
                return
            }

            if (labelUpdates.length > 0) {
                await Promise.all(
                    labelUpdates.map((m) =>
                        updatePaymentMethod(companyId, m.id, {
                            code: m.code,
                            label: m.label.trim(),
                            type: m.type,
                            is_active: m.is_active,
                        })
                    )
                )
            }

            const payload = methods.map((m, idx) => ({
                payment_method_id: m.id,
                is_enabled: m.is_enabled,
                display_order: idx,
            }))
            await updateOutletPaymentMethods(companyId, outletId, payload)
            await loadMethods()
            setSnackbar({
                open: true,
                message: String(t("settings.paymentMethods.toast.saved")),
                severity: "success",
            })
        } catch (e: any) {
            setSnackbar({
                open: true,
                message: String(t("settings.paymentMethods.toast.saveError")),
                severity: "error",
            })
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <>
                <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <PaymentIcon sx={{ color: "#6366f1" }} />
                    {t("settings.paymentMethods.title")}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            </>
        )
    }

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                    <PaymentIcon sx={{ color: "#6366f1" }} />
                    {t("settings.paymentMethods.title")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateDialog}
                        sx={{ textTransform: "none" }}
                    >
                        {t("settings.paymentMethods.add")}
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={
                            saving ? (
                                <CircularProgress size={16} />
                            ) : (
                                <SaveIcon />
                            )
                        }
                        disabled={!dirty || saving}
                        onClick={handleSave}
                        sx={{
                            textTransform: "none",
                            bgcolor: "#6366f1",
                            "&:hover": { bgcolor: "#4f46e5" },
                        }}
                    >
                        {t("common.save")}
                    </Button>
                </Box>
            </Box>

            {methods.length === 0 ? (
                <Alert severity="info">
                    {t("settings.paymentMethods.empty")}
                </Alert>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {methods.map((m) => (
                        <Box
                            key={m.id}
                            draggable={!saving}
                            onDragStart={() => handleDragStart(m.id)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => handleDrop(m.id)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                py: 1,
                                px: 1.5,
                                borderRadius: 1,
                                bgcolor: m.is_enabled ? "#f0fdf4" : "#fafafa",
                                border: "1px solid",
                                borderColor: m.is_enabled
                                    ? "#bbf7d0"
                                    : "#e5e7eb",
                                transition: "all 0.15s",
                                cursor: saving ? "default" : "grab",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                }}
                            >
                                <DragIndicatorIcon
                                    fontSize="small"
                                    color="action"
                                />
                                <Box
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "#eef2ff",
                                        color: "#374151",
                                    }}
                                >
                                    {typeIcons[m.type] || typeIcons.other}
                                </Box>
                                <TextField
                                    variant="standard"
                                    value={m.label}
                                    onChange={(e) =>
                                        handleLabelChange(m.id, e.target.value)
                                    }
                                    disabled={saving}
                                    sx={{ minWidth: 170 }}
                                    inputProps={{
                                        style: {
                                            fontWeight: 500,
                                            fontSize: "0.95rem",
                                        },
                                    }}
                                />
                                <Chip
                                    label={
                                        getPaymentTypeLabel(m.type) || m.type
                                    }
                                    size="small"
                                    sx={{
                                        bgcolor:
                                            typeColors[m.type] ||
                                            typeColors.other,
                                        color: "#fff",
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                        height: 22,
                                    }}
                                />
                                {!m.is_active && (
                                    <Chip
                                        label={t(
                                            "settings.paymentMethods.inactive"
                                        )}
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                        sx={{ fontSize: "0.7rem", height: 22 }}
                                    />
                                )}
                            </Box>
                            <Switch
                                checked={m.is_enabled}
                                onChange={() => handleToggle(m.id)}
                                disabled={saving}
                                color="success"
                            />
                        </Box>
                    ))}
                </Box>
            )}

            <Dialog
                open={showCreateDialog}
                onClose={() => !creating && setShowCreateDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {t("settings.paymentMethods.dialog.title")}
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        <TextField
                            label={String(
                                t("settings.paymentMethods.fields.label")
                            )}
                            fullWidth
                            value={createForm.label}
                            onChange={(e) =>
                                setCreateForm((prev) => ({
                                    ...prev,
                                    label: e.target.value,
                                }))
                            }
                            disabled={creating}
                        />
                        <TextField
                            label={String(
                                t("settings.paymentMethods.fields.code")
                            )}
                            fullWidth
                            value={createForm.code}
                            helperText={t(
                                "settings.paymentMethods.fields.codeHelper"
                            )}
                            onChange={(e) =>
                                setCreateForm((prev) => ({
                                    ...prev,
                                    code: e.target.value,
                                }))
                            }
                            disabled={creating}
                        />
                        <FormControl fullWidth>
                            <InputLabel>
                                {t("settings.paymentMethods.fields.type")}
                            </InputLabel>
                            <Select
                                label={String(
                                    t("settings.paymentMethods.fields.type")
                                )}
                                value={createForm.type}
                                onChange={(e) =>
                                    setCreateForm((prev) => ({
                                        ...prev,
                                        type: String(e.target.value || "cash"),
                                    }))
                                }
                                disabled={creating}
                            >
                                {paymentTypeValues.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {getPaymentTypeLabel(type)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowCreateDialog(false)}
                        disabled={creating}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateMethod}
                        disabled={creating}
                        startIcon={
                            creating ? (
                                <CircularProgress size={14} />
                            ) : (
                                <AddIcon />
                            )
                        }
                    >
                        {t("settings.paymentMethods.dialog.create")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    )
}

export default PaymentMethodsSection
